import {
  cacheDecorator,
  networkFirstAndCacheDecorator,
  updateCache,
} from '@ds/swymkit/tools'

import { swymFetch } from '~/services/fetch-swym'

import type { IQuestion } from '~/entities/content/question/interfaces/question'

import type { IUpdateParams } from '~/features-shared/content/api/interfaces/params'
import { contentParser } from '~/features-shared/content/api/parser-contents'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { ISwymContent } from '~/features-shared/content/interfaces/swym-content'
import type { TagActions } from '~/features-shared/content-derived-type/utils/derived-types'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

import type { ICRUDService, ICRUDServiceParams } from '~/interfaces/crud-service'
import type { SwymResponse } from '~/interfaces/services-dto'

/** Parameters to create a question */
interface ICreationQuestionParams extends Omit<ICRUDServiceParams, 'subject_uri'> {
  /** Title of the Question */
  title?: string
  /** Community of the question, it will be created in this community */
  community_subject_uri?: SUBJECT_6W_URI
  /** Question "message body" / description */
  message?: string
  /** Tags to add / remove */
  taggingActions?: TagActions
  /** Is this question a draft or not ? */
  published?: boolean
}

/** Parameters to read a question */
type IReadQuestionParams = Pick<ICRUDServiceParams, 'subject_uri'>

/**
 * Update parameters for Question
 * @see ICRUDServiceParams
 * @see ICreationQuestionParams
 */
type IUpdateQuestionParams = ICRUDServiceParams & Omit<ICreationQuestionParams, 'published'> & IUpdateParams

/** Parameters for delete */
interface IDeleteQuestionParams extends ICRUDServiceParams {
  community_subject_uri?: SUBJECT_6W_URI
}

type QuestionCRUDService = ICRUDService<
  IQuestion,
  ICreationQuestionParams,
  IReadQuestionParams,
  IUpdateQuestionParams,
  IDeleteQuestionParams
>

/** A CRUD service for question */
export const QUESTION_CRUD_API: QuestionCRUDService = {
  create(params, tracker) {
    return swymFetch.post('/api/IquestionsQuestions/add', {
      json: {
        params: {
          title: params.title,
          community_id: params.community_subject_uri,
          question: params.message,
          published: params.published ? 1 : 0,
          ...(params.taggingActions ? { taggingActions6w: params.taggingActions } : undefined),
        },
      },
      tracker,
    })
      .json<SwymResponse<ISwymContent>>()
      .then(contentParser<IQuestion>)
      .then(response => updateCache(`question-${response.result.subject_uri}`, response))
  },

  read(params, tracker) {
    return cacheDecorator(
      `question-${params.subject_uri}`,
      () => swymFetch
        .post('/api/IquestionsQuestions/getquestioninfos', {
          json: {
            params: {
              iQuestions_id: params.subject_uri,
            },
          },
          tracker,
        })
        .json<SwymResponse<ISwymContent>>()
        .then(contentParser<IQuestion>)
        .then((response) => {
          response.result.model = IContentModel.Question
          return response
        }),
      2000,
    )
  },

  update(params, tracker) {
    return networkFirstAndCacheDecorator(`question-${params.subject_uri}`, () => swymFetch
      .post('/api/IquestionsQuestions/update', {
        json: {
          params: {
            title: params.title,
            qna_id: params.subject_uri,
            question: params.description,
            published: params.published ? 1 : 0,
            ...(params.taggingActions ? { taggingActions6w: params.taggingActions } : undefined),
          },
        },
        tracker,
      })
      .json<SwymResponse<ISwymContent>>()
      .then(response => contentParser<IQuestion>(response))
      .then((response) => {
        response.result.model = IContentModel.Question
        return response
      }))
  },

  delete(params, tracker) {
    return swymFetch.post('/api/community/deletecontribution', {
      json: {
        params: {
          iquestionIds: [params.subject_uri],
          community_id: params.community_subject_uri,
        },
      },
      tracker,
    })
      .json<SwymResponse<void>>()
  },
}
